import { capitalize } from './naming'
import { isNonNaNNumber } from '@iotv/iotv-v3-types'

export const makePresentable = (str: string, valueOverrides?: { [str: string]: string }): string => {
  if (valueOverrides?.[str]) return valueOverrides[str]
  return capitalize(str)
}

/**
 * Converts a noun into its plural form
 *
 * Use the optional count to auto pluralize and keep horrible code out of components
 */
export const pluralize = (str: string, count?: number) => {
  const plural = pluralNoun(str)
  let res = str
  if (isNonNaNNumber(count)) {
    if (count > 1 || count === 0) {
      res = plural
    }
  } else {
    res = plural
  }
  return res
}

/**
 * Converts a noun into its plural form
 *
 * There are still several issues and cases it can't handle
 *
 * https://byjus.com/english/plural-noun/
 * @param noun word to pluralize
 * @returns plural
 */
const pluralNoun = (noun: string) => {
  const vowels = ['a', 'e', 'i', 'o', 'u']
  // nnnnXn
  const precedingLastCharacter = noun.substring(noun.length - 2, noun.length - 1)
  // nnnnXX
  const lastTwoCharacters = noun.substring(noun.length - 2)
  // nnnnnX
  const lastCharacter = noun.substring(noun.length - 1)

  // if the word ends in `is` — replace with `es`
  if (lastTwoCharacters === 'is') return noun.replace(/is$/, 'es')

  // if the word ends in `us` — replace with `i`
  if (lastTwoCharacters === 'us') return noun.replace(/us$/, 'i')

  // if the word ends in `y` preceded with a vowel — add `s`
  if (lastCharacter === 'y' && vowels.includes(precedingLastCharacter)) return noun + 's'

  // if the word ends in `y` preceded with a consonant — replace with `ies`
  if (lastCharacter === 'y' && !vowels.includes(precedingLastCharacter)) return noun.replace(/y$/, 'ies')

  // if the word ends in `o` — add `es`
  if (lastCharacter === 'o') return noun + 'es'

  // if the word ends in `f` or `fe` — replace with `ves`
  if (lastCharacter === 'f' || lastTwoCharacters === 'fe') return noun.replace(/f$|fe$/, 'ves')

  // if the word ends in `x` or `s` — add `es`
  if (lastCharacter === 'x' || lastCharacter === 's') return noun + 'es'

  // default rule / fallback — add `s`
  return noun + 's'
}

/** Formats a string with quantity in mind
 * @example (1, "time") => "1 time"; (2, "time") => "2 times"
 * */
export const quantityFormatter = (quantity: number, countWord: string) => {
  return `${quantity} ${quantity === 1 ? countWord : pluralize(countWord)}`
}

// extends { name: string }
export const sortHumanReadable = <T>(list: T[], key: keyof T): T[] => {
  return list.sort((a, b) => {
    let aRes = a[key]
    let bRes = b[key]

    if (typeof aRes === 'string' && typeof bRes === 'string') {
      return aRes.localeCompare(bRes, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    }
    return 0
  })
}
